import {
  toWSDateString,
  WSButton,
  WSCard,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { queriesPayerRows, queriesRecurringInvoices } from "@query";
import { selectorSearchRowName } from "../../shared/selectors/selectorSearchRowName";
import { AlertEnableTaxWithholdings } from "../../modules/TaxWithholdings/components/AlertEnableTaxWithholdings";
import { WSQueries } from "../../query/WSQuery";
import { Layout } from "../../shared/components/Layout";
import { FirstInvoiceBanner } from "../components/FirstInvoiceBanner/FirstInvoiceBanner";
import { InvoiceTemplateTotals } from "../components/InvoiceTempalteTotals";
import { useInvoiceFormGoBack } from "../utils/invoicesFormUtils";

export const RouteInvoiceTemplateConfirmation: React.FC<
  RouteComponentProps<{
    invoiceTemplateId: string;
  }>
> = ({
  match: {
    params: { invoiceTemplateId }
  }
}) => {
  const history = useHistory();
  const queryRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery({
      recurringInvoiceId: invoiceTemplateId
    });
  const payerId = queryRecurringInvoice.data?.payerId;
  // Name off the payer search row's server-resolved `profile.resolvedName`,
  // the same string the invoice tables show for this client.
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: { id: { eq: payerId! } },
      page: { size: 1 }
    },
    {
      enabled: !!payerId,
      retry: false
    }
  );
  const goBack = useInvoiceFormGoBack();

  return (
    <Layout title="Confirmation" onClose={goBack}>
      <WSQueries
        queries={{
          queryRecurringInvoice,
          queryPayerRow
        }}
      >
        {({
          queryRecurringInvoice: { data: recurringInvoice },
          queryPayerRow: { data: payerRows }
        }) => {
          const payerRow = payerRows.data?.[0];
          const payerName = payerRow ? selectorSearchRowName(payerRow) : "";
          const isSeries = !!recurringInvoice.schedule?.frequency;
          const firstOccurrenceDate =
            recurringInvoice.schedule?.scheduleDates?.[0]?.date ??
            recurringInvoice.nextOccurrenceDate;

          return (
            <WSList gap="XL">
              <FirstInvoiceBanner />

              <WSCard>
                <WSText.Heading4 mb="M">{payerName}</WSText.Heading4>

                <WSText.Heading5 mb="XS">Invoice is scheduled</WSText.Heading5>

                <WSText color="gray500" mb="XL">
                  {isSeries
                    ? `First invoice will be sent ${toWSDateString(
                        firstOccurrenceDate,
                        "monthDayYear"
                      )}`
                    : `Invoice will be sent ${toWSDateString(
                        firstOccurrenceDate,
                        "monthDayYear"
                      )}`}
                </WSText>
                <WSButton.Secondary
                  fullWidth
                  onClick={() => {
                    history.push(
                      `/member/invoices/template/${invoiceTemplateId}/edit`
                    );
                  }}
                >
                  {isSeries ? "Edit series" : "Edit invoice"}
                </WSButton.Secondary>
              </WSCard>

              <WSCard>
                <WSText.Heading5 mb="M" data-testid="paymentTimingText">
                  Payment timing
                </WSText.Heading5>
                <WSText color="gray600">
                  Funds are automatically deposited within 7 business days of
                  payment.
                </WSText>
              </WSCard>

              <WSCard>
                <WSList gap="2XL">
                  <WSText.Heading5>Summary</WSText.Heading5>

                  <InvoiceTemplateTotals recurringInvoice={recurringInvoice} />

                  <AlertEnableTaxWithholdings />

                  <WSButton
                    onClick={() => {
                      history.push("/member/invoices");
                    }}
                    fullWidth
                  >
                    Back to invoices
                  </WSButton>
                </WSList>
              </WSCard>
            </WSList>
          );
        }}
      </WSQueries>
    </Layout>
  );
};
